import React, { useState, useEffect } from 'react';
import { Edit2, TrendingUp, Plus, Search, X, Trash2, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { ref, onValue, update, remove, get } from "firebase/database";
import { db } from "../firebase";
import TradingControlModal from './TradingControlModal';
import Swal from 'sweetalert2'; 
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [amountInput, setAmountInput] = useState('');
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  
  // Settings States
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsData, setSettingsData] = useState({ password: '', trc: '' });

  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 20;

  useEffect(() => {
    const usersRef = ref(db, 'users');
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const usersList = Object.keys(data).map(key => ({
          dbKey: key,
          ...data[key]
        }));
        setUsers(usersList);
      }
    });
  }, []);

  const handleUpdateSettings = async () => {
    if (!selectedUser) return;
    try {
 const updates = {};
updates[`users/${selectedUser.dbKey}/password`] = settingsData.password;
updates[`users/${selectedUser.dbKey}/paymentMethod/address`] = settingsData.trc;

await update(ref(db), updates);
      Swal.fire('Success', 'User details updated!', 'success');
      setIsSettingsModalOpen(false);
    } catch (e) {
      Swal.fire('Error', 'Update failed', 'error');
    }
  };

  const handleDeleteUser = async (dbKey) => {
    const result = await Swal.fire({
  
      text: "Do you want to delete this user ?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff4d4d',
      confirmButtonText: 'Yes!'
    });

    if (result.isConfirmed) {
      try {
        await remove(ref(db, `users/${dbKey}`));
        Swal.fire('Deleted!', 'success');
      } catch (e) { console.error(e); }
    }
  };

  const handleUpdateBalance = async (type) => {
    if (!selectedUser || !amountInput) return;
    try {
      const userRef = ref(db, `users/${selectedUser.dbKey}`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        const currentBalance = parseFloat(snapshot.val().balance || 0);
        const changeAmount = parseFloat(amountInput);
        let finalBalance = type === 'add' ? currentBalance + changeAmount : currentBalance - changeAmount;
        await update(userRef, { balance: finalBalance.toFixed(2) });
        Swal.fire({ title: 'Success!', text: `Updated to: ${finalBalance.toFixed(2)}`, icon: 'success', timer: 1500, showConfirmButton: false });
        setIsBalanceModalOpen(false);
        setAmountInput('');
      }
    } catch (e) { console.error(e); }
  };

  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  return (
    <div className="users-page-container">
      <div className="users-top-nav">
        <div className="title-search-group">
          <h2 className="page-title">Users Management</h2>
          <div className="nav-search-wrapper">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
      </div>

      <div className="full-width-table-area">
        <table className="pro-data-table">
          <thead>
            <tr>
              <th>USERNAME</th>
              <th>BALANCE (USDT)</th>
              <th>TRADE CONTROL</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => (
              <tr key={user.dbKey}>
                <td className="u-name">{user.username}</td>
                <td className="u-bal">
                  <span className="bal-text">${parseFloat(user.balance || 0).toFixed(2)}</span>
                  <Edit2 size={14} className="edit-icon-small" onClick={() => {
                    setSelectedUser(user);
                    setAmountInput('');
                    setIsBalanceModalOpen(true);
                  }}/>
                </td>
                <td>
                  <button className="pro-control-btn" onClick={() => {
                    setSelectedUser(user);
                    setIsTradeModalOpen(true);
                  }}>
                    <TrendingUp size={14} /> CONTROL
                  </button>
                </td>
                <td>
                  <label className="pro-switch">
                    <input type="checkbox" defaultChecked={user.status !== 'blocked'} />
                    <span className="pro-slider"></span>
                  </label>
                </td>
                <td className="pro-actions">
                  <button className="btn-act settings" onClick={() => {
                    setSelectedUser(user);
                    setSettingsData({ password: user.password || '', trc: user.trc || '' });
                    setIsSettingsModalOpen(true);
                  }}>
                    <Settings size={18} color="#aaa" />
                  </button>
                  <button className="btn-act delete" onClick={() => handleDeleteUser(user.dbKey)}>
                    <Trash2 size={18} color="#ff4d4d" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination-footer">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}><ChevronLeft /></button>
        <span>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}><ChevronRight /></button>
      </div>

      {/* BALANCE MODAL */}
      {isBalanceModalOpen && (
        <div className="modal-overlay">
          <div className="balance-modal-card">
            <div className="modal-header">
              <h3>Balance Adjust</h3>
              <X className="close-btn" onClick={() => setIsBalanceModalOpen(false)} />
            </div>
            <div className="modal-body">
              <p className="user-info">User: <strong>{selectedUser?.username}</strong></p>
              <p className="current-bal">Current: <span>${parseFloat(selectedUser?.balance || 0).toFixed(2)}</span></p>
              <div className="input-group-bal">
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                />
              </div>
              <div className="action-buttons-grid">
                <button className="btn-bal-add" onClick={() => handleUpdateBalance('add')}><Plus size={16} /> ADD</button>
                <button className="btn-bal-sub" onClick={() => handleUpdateBalance('sub')}><Trash2 size={16} /> DEDUCT</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SETTINGS MODAL */}
      {isSettingsModalOpen && (
        <div className="modal-overlay">
          <div className="settings-modal-card">
            <div className="modal-header">
              <h3>User Credentials</h3>
              <X className="close-btn" onClick={() => setIsSettingsModalOpen(false)} />
            </div>
            <div className="modal-body">
              <div className="input-field-group">
                <label>Password</label>
                <input 
                  type="text" 
                  value={settingsData.password} 
                  onChange={(e) => setSettingsData({...settingsData, password: e.target.value})}
                />
              </div>
              <div className="input-field-group">
                <label>TRC20 Address</label>
                <input 
                  type="text" 
                  value={settingsData.trc} 
                  onChange={(e) => setSettingsData({...settingsData, trc: e.target.value})}
                />
              </div>
              <button className="save-settings-btn" onClick={handleUpdateSettings}>SAVE CHANGES</button>
            </div>
          </div>
        </div>
      )}

      {isTradeModalOpen && (
        <TradingControlModal
          user={selectedUser}
          onClose={() => setIsTradeModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Users;